import { format } from 'date-fns';
import { CheckCircleIcon, ClockIcon, XCircleIcon, ShoppingBagIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../config/currency';
// import { useTheme } from '../context/ThemeContext';

interface OrderItem {
  id: number;
  menuItem: { 
    id: number; 
    name: string;
    price: number;
  };
  quantity: number;
  price: number;
  comment?: string;
  toppings?: { id: number; name: string }[];
}

interface Order {
  id: number;
  orderId: number;
  status: string;
  total: number;
  createdAt: string;
  itemCount: number;
  customerType?: string;
  customerName?: string;
  customerPhone?: string;
  items?: OrderItem[];
}

interface OrderLineTimelineProps {
  orders: Order[];
  onOrderClick?: (orderId: number) => void;
}

const OrderLineTimeline: React.FC<OrderLineTimelineProps> = ({ orders, onOrderClick }) => {

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'PENDING':
      case 'ACCEPTED':
      case 'PREPARING':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ShoppingBagIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ACCEPTED':
      case 'PREPARING':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'READY':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600" />
      
      {/* Orders */}
      <div className="space-y-4">
        {orders.map((order) => (
          <div 
            key={order.id} 
            className="relative flex items-start cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg transition-colors"
            onClick={() => onOrderClick?.(order.orderId)}
          >
            {/* Timeline dot */}
            <div className="relative z-10 flex items-center justify-center w-12 h-12">
              <div className="bg-white dark:bg-gray-900 rounded-full p-1">
                {getStatusIcon(order.status)}
              </div>
            </div>

            {/* Order content */}
            <div className="flex-1 ml-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Order #{order.orderId}
                  </h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Time:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {format(new Date(order.createdAt), 'HH:mm:ss')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Items:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">{order.itemCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Total:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Customer:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {order.customerType === 'guest' ? order.customerPhone : order.customerName || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Show order items if available */}
                {order.items && order.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      {order.items.slice(0, 3).map((item, index) => (
                        <div key={index}>
                          {item.quantity}x {item.menuItem?.name || (item as any).name || 'Unknown Item'}
                          {item.toppings && item.toppings.length > 0 && (
                            <span className="text-gray-500"> (+{item.toppings.length} toppings)</span>
                          )}
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <div className="text-gray-500 italic">
                          +{order.items.length - 3} more items...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderLineTimeline;